import { Reducer } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { TabErrors, DeleteConfirmationState } from "./types";
import { isTemp } from "./utils";

export interface TabsState {
  localTabs: TabData[];
  tabErrors: TabErrors;
  newTabName: string;
  newTabError: string | null;
  isAddingNewTab: boolean;
  hoveredTabId: string | null;
  hoveredNewItem: boolean;
  draggedIndex: number | null;
  dragOverIndex: number | null;
  deleteConfirmation: DeleteConfirmationState | null;
}

export type TabsAction =
  | { type: "SYNC_FROM_PROPS"; tabs: TabData[] }
  | { type: "SET_NEW_TAB_NAME"; value: string }
  | { type: "SET_NEW_TAB_ERROR"; value: string | null }
  | { type: "START_ADD" }
  | { type: "CANCEL_ADD" }
  | { type: "ADD_OPTIMISTIC_TAB"; tab: TabData }
  | { type: "REMOVE_TAB"; tabId: string }
  | { type: "UPDATE_TAB_NAME"; id: string; label: string }
  | { type: "REVERT_TAB_NAME"; id: string; label: string }
  | { type: "SET_TAB_ERROR"; id: string; message: string }
  | { type: "CLEAR_TAB_ERROR"; id: string }
  | { type: "HOVER_TAB"; tabId: string | null }
  | { type: "HOVER_NEW_ITEM"; value: boolean }
  | { type: "DRAG_START"; index: number }
  | { type: "DRAG_END" }
  | { type: "DRAG_OVER"; index: number | null }
  | { type: "REORDER"; from: number; to: number }
  | {
      type: "OPEN_DELETE_CONFIRM";
      payload: { tabId: string; tabName: string; hasContent: boolean };
    }
  | { type: "CLOSE_DELETE_CONFIRM" }
  | { type: "SET_DELETE_HAS_CONTENT"; hasContent: boolean };

export const initialState = (tabs: TabData[]): TabsState => ({
  localTabs: tabs,
  tabErrors: {},
  newTabName: "",
  newTabError: null,
  isAddingNewTab: false,
  hoveredTabId: null,
  hoveredNewItem: false,
  draggedIndex: null,
  dragOverIndex: null,
  deleteConfirmation: null,
});

export const tabsReducer: Reducer<TabsState, TabsAction> = (state, action) => {
  switch (action.type) {
    case "SYNC_FROM_PROPS": {
      // Close add UI if we were adding and now have more tabs (new tab was created)
      const shouldCloseAdd =
        state.isAddingNewTab && action.tabs.length > state.localTabs.length;

      // Use only server tabs - no optimistic UI
      return {
        ...state,
        localTabs: action.tabs,
        isAddingNewTab: shouldCloseAdd ? false : state.isAddingNewTab,
        newTabName: shouldCloseAdd ? "" : state.newTabName,
      };
    }
    case "SET_NEW_TAB_NAME":
      return {
        ...state,
        newTabName: action.value,
        newTabError: action.value.trim() ? null : state.newTabError,
      };
    case "SET_NEW_TAB_ERROR":
      return { ...state, newTabError: action.value };
    case "START_ADD":
      return { ...state, isAddingNewTab: true };
    case "CANCEL_ADD":
      return {
        ...state,
        isAddingNewTab: false,
        newTabName: "",
        newTabError: null,
      };
    case "ADD_OPTIMISTIC_TAB":
      // Keep the add UI open while waiting for server response
      // The input will be disabled and skeleton will show
      return {
        ...state,
        isAddingNewTab: true, // Keep it open
        newTabName: state.newTabName, // Keep the name
        newTabError: null,
      };
    case "REMOVE_TAB":
      return {
        ...state,
        localTabs: state.localTabs.filter((t) => t.id !== action.tabId),
      };
    case "UPDATE_TAB_NAME": {
      const tabErrors = { ...state.tabErrors };
      if (action.label.trim()) delete tabErrors[action.id];
      return {
        ...state,
        tabErrors,
        localTabs: state.localTabs.map((t) =>
          t.id === action.id ? { ...t, label: action.label } : t,
        ),
      };
    }
    case "REVERT_TAB_NAME":
      return {
        ...state,
        localTabs: state.localTabs.map((t) =>
          t.id === action.id ? { ...t, label: action.label } : t,
        ),
      };
    case "SET_TAB_ERROR":
      return {
        ...state,
        tabErrors: { ...state.tabErrors, [action.id]: action.message },
      };
    case "CLEAR_TAB_ERROR": {
      const { [action.id]: _, ...rest } = state.tabErrors;
      return { ...state, tabErrors: rest };
    }
    case "HOVER_TAB":
      return { ...state, hoveredTabId: action.tabId };
    case "HOVER_NEW_ITEM":
      return { ...state, hoveredNewItem: action.value };
    case "DRAG_START":
      return { ...state, draggedIndex: action.index };
    case "DRAG_END":
      return { ...state, draggedIndex: null, dragOverIndex: null };
    case "DRAG_OVER":
      return { ...state, dragOverIndex: action.index };
    case "REORDER": {
      const copy = [...state.localTabs];
      const [moved] = copy.splice(action.from, 1);
      copy.splice(action.to, 0, moved);
      return { ...state, localTabs: copy };
    }
    case "OPEN_DELETE_CONFIRM":
      return { ...state, deleteConfirmation: { ...action.payload } };
    case "CLOSE_DELETE_CONFIRM":
      return { ...state, deleteConfirmation: null };
    case "SET_DELETE_HAS_CONTENT":
      return state.deleteConfirmation
        ? {
            ...state,
            deleteConfirmation: {
              ...state.deleteConfirmation,
              hasContent: action.hasContent,
            },
          }
        : state;
    default:
      return state;
  }
};
